import { apiAddCredits, apiRedeemCode, apiRefreshCredits } from '@/api/payment'
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex'
import { initRequestHelper, isAlreadyPending } from '../root'
import { PaymentState } from '../types/payment'
import { RootState } from '../types/root'

const getDefaultState = (): PaymentState => {
  return {
    isPaymentValid: false,
    isPaymentComplete: false,
    creditBalanceSummary: undefined,
  }
}

const moduleState = getDefaultState()

const moduleGetters: GetterTree<PaymentState, RootState> = {
  isPaymentValid(state) {
    return state.isPaymentValid
  },
  isPaymentComplete(state) {
    return state.isPaymentComplete
  },
  redeemCodeRequest(_state, _getters, rootState) {
    return rootState.requests.redeemCode
  },
  addCreditsRequest(_state, _getters, rootState) {
    return rootState.requests.addCredits
  },
  creditsInCents(state) {
    if (!state.creditBalanceSummary) {
      return null
    }

    return state.creditBalanceSummary.total_remaining_credits
  },

  balanceSummary(state) {
    if (!state.creditBalanceSummary) {
      return null
    }

    return state.creditBalanceSummary.balance_summary
  },
}

const moduleMutations: MutationTree<PaymentState> = {
  setIsPaymentValid(state, isValid) {
    state.isPaymentValid = isValid
  },
  setIsPaymentComplete(state, isComplete) {
    state.isPaymentComplete = isComplete
  },
  setCreditBalanceSummary(state, newCreditBalanceSummary) {
    state.creditBalanceSummary = newCreditBalanceSummary
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  },
}

export const moduleActions: ActionTree<PaymentState, RootState> = {
  async redeemCode({ commit, dispatch, getters }, code: string) {
    if (isAlreadyPending(getters.redeemCodeRequest)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'redeemCode')
    try {
      await apiRedeemCode(code)
      dispatch('refreshCredits')
      setSuccess('Coupon successfully applied.')
    } catch (err: any) {
      setFailure(err.message)
    }
  },
  async resetPaymentState({ commit }) {
    commit('resetState')
  },
  async addCredits({ commit, dispatch, getters }, amountInCents: number) {
    if (isAlreadyPending(getters.addCreditsRequest)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'addCredits')
    try {
      await apiAddCredits(amountInCents)
      dispatch('refreshCredits')
      setSuccess('Credits purchased successfully.')
    } catch (err: any) {
      setFailure(err.message)
    }
  },
  async refreshCredits({ commit, dispatch, getters }) {
    if (isAlreadyPending(getters.refreshCreditsRequest)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'refreshCredits')
    try {
      const result = await apiRefreshCredits()
      commit('setCreditBalanceSummary', result)
      setSuccess()
    } catch (err: any) {
      setFailure(err.message)
    }
  },
}

const payment: Module<PaymentState, RootState> = {
  namespaced: true,
  state: moduleState,
  getters: moduleGetters,
  mutations: moduleMutations,
  actions: moduleActions,
}

export default payment
